import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Pencil, ArrowRightLeft, UserPlus, Globe, FlaskConical } from "lucide-react";
import { api } from "@/api/client";
import EditOrganizationDialog, {
  type Organization,
} from "@/features/organizations/EditOrganizationDialog";
import TransferAdAccountDialog from "@/features/organizations/TransferAdAccountDialog";
import GrantDialog from "@/features/organizations/GrantDialog";

interface AdAccount {
  id: string;
  organizationId: string;
  metaAccountId: string;
  name: string;
  currency: string | null;
  sandbox: boolean;
}

export default function OrganizationDetailPage() {
  const { id = "" } = useParams();
  const [editing, setEditing] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [granting, setGranting] = useState<AdAccount | null>(null);

  const orgQ = useQuery({
    queryKey: ["organization", id],
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/v1/organizations/{id}",
        { params: { path: { id } } }
      );
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Organization;
    },
  });

  // All ad-accounts the current user can see; filter to this org client-side.
  const acctsQ = useQuery({
    queryKey: ["ad-accounts"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/ad-accounts");
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as AdAccount[];
    },
  });
  const ownedAccts = (acctsQ.data ?? []).filter((a) => a.organizationId === id);

  const org = orgQ.data;

  return (
    <div className="space-y-6">
      {editing && org && (
        <EditOrganizationDialog org={org} onClose={() => setEditing(false)} />
      )}
      {transferring && org && (
        <TransferAdAccountDialog
          toOrgId={org.id}
          toOrgName={org.name}
          onClose={() => setTransferring(false)}
        />
      )}
      {granting && (
        <GrantDialog
          organizationId={id}
          adAccountId={granting.id}
          adAccountName={granting.name}
          onClose={() => setGranting(null)}
        />
      )}

      <div>
        <Link to="/organizations" className="text-xs text-muted-foreground hover:underline">
          ← 返回公司列表
        </Link>
        <div className="flex items-start justify-between gap-3 mt-2">
          <div>
            <h1 className="text-2xl font-semibold">
              {org?.name ?? "公司"}
              {org && (
                <span className={
                  "ml-3 text-xs px-2 py-0.5 rounded align-middle " +
                  (org.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-700")
                }>
                  {org.status}
                </span>
              )}
            </h1>
            <div className="text-sm text-muted-foreground mt-1 font-mono">
              {org?.emailDomain}
            </div>
          </div>
          {org && (
            <button
              onClick={() => setEditing(true)}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-1.5"
            >
              <Pencil className="w-3.5 h-3.5" /> 编辑
            </button>
          )}
        </div>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-medium text-lg">广告账户 ({ownedAccts.length})</h2>
          <button
            onClick={() => setTransferring(true)}
            className="rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium hover:opacity-90 flex items-center gap-1.5"
          >
            <ArrowRightLeft className="w-3.5 h-3.5" /> 转入广告账户
          </button>
        </div>

        {acctsQ.isLoading && (
          <div className="rounded-lg border border-border bg-background p-6 text-center text-sm text-muted-foreground">
            加载中…
          </div>
        )}

        {!acctsQ.isLoading && ownedAccts.length === 0 && (
          <div className="rounded-lg border border-border bg-background p-6 text-center text-sm text-muted-foreground">
            该公司暂无广告账户(可从右上角转入)
          </div>
        )}

        {ownedAccts.length > 0 && (
          <div className="rounded-lg border border-border bg-background overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">名称</th>
                  <th className="text-left px-4 py-2 font-medium">act_id</th>
                  <th className="text-left px-4 py-2 font-medium">币种</th>
                  <th className="text-left px-4 py-2 font-medium">类型</th>
                  <th className="text-right px-4 py-2 font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {ownedAccts.map((a) => (
                  <tr key={a.id} className="border-t border-border">
                    <td className="px-4 py-2 font-medium">
                      <Link to={`/ad-accounts/${a.id}`} className="text-primary hover:underline">
                        {a.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">ACT_{a.metaAccountId}</td>
                    <td className="px-4 py-2">{a.currency ?? "—"}</td>
                    <td className="px-4 py-2">
                      {a.sandbox
                        ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                            <FlaskConical className="w-3 h-3" /> 沙盒
                          </span>
                        : <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Globe className="w-3 h-3" /> 生产
                          </span>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => setGranting(a)}
                        className="text-xs text-primary hover:underline flex items-center gap-1 ml-auto"
                      >
                        <UserPlus className="w-3 h-3" /> 授权用户
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
